import { loadStripe, type Stripe } from "@stripe/stripe-js";

/**
 * Carga de Stripe.js, una sola vez y solo cuando hace falta.
 *
 * El guion se descarga de `js.stripe.com` en tiempo de ejecución y no se
 * empaqueta. Es requisito de Stripe: el formulario de tarjeta vive en un iframe
 * servido por ellos, así que **el número de tarjeta nunca entra en nuestro
 * dominio ni en nuestro código**. Es lo que mantiene todo el sistema fuera del
 * alcance de PCI-DSS.
 *
 * La llave publicable es pública por diseño —viaja en el paquete y cualquiera la
 * ve en el inspector—. Solo sirve para tokenizar una tarjeta; no consulta cobros
 * ni mueve dinero.
 */
const LLAVE = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;

let promesa: Promise<Stripe | null> | null = null;

/**
 * Devuelve `null` si no hay llave configurada.
 *
 * Es el caso de los emuladores y de las pruebas, donde el backend responde con
 * la pasarela de simulación y no hay tarjeta que pedir. Cargar Stripe.js ahí
 * sería descargar un guion para nada.
 */
export function stripeCargado(): Promise<Stripe | null> {
  if (!LLAVE) return Promise.resolve(null);
  if (!promesa) promesa = loadStripe(LLAVE);
  return promesa;
}

/** Si hay llave publicable, y por tanto si esta build puede cobrar con tarjeta. */
export const HAY_STRIPE = Boolean(LLAVE);

/**
 * Aspecto de los formularios de Stripe, compartido por todos.
 *
 * Los pinta Stripe dentro de un iframe suyo, así que no los alcanza nuestra hoja
 * de estilos y hay que declararlo aquí. En un solo sitio porque son dos —pagar y
 * guardar una tarjeta— y si cada uno llevara el suyo acabarían distintos.
 */
export const APARIENCIA_STRIPE = {
  theme: "night",
  variables: {
    colorPrimary: "#EC6907",
    colorBackground: "#0d0d0d",
    colorText: "#f2f2f2",
    fontFamily: "inherit",
    borderRadius: "10px",
  },
} as const;

/** Español de América Latina, para que los textos de Stripe no salgan en inglés. */
export const IDIOMA_STRIPE = "es-419" as const;
